import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { Flight } from '../../types/flight';
import { useFlightsStore } from '../../stores/flightsStore';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  flight: Flight | null;
};

export const EditFlightModal = ({ isOpen, onClose, flight }: Props) => {
  const { translations } = useLanguage();
  const { updateFlight } = useFlightsStore();
  const [formData, setFormData] = useState<Partial<Flight>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (flight) {
      setFormData({ ...flight });
    }
  }, [flight]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.pnrNumber) {
      newErrors.pnrNumber = translations?.flightValidation?.pnrRequired || 'PNR Number is required';
    }
    if (!formData.outboundFlightNumber) {
      newErrors.outboundFlightNumber = translations?.flightValidation?.outboundFlightRequired || 'Outbound flight number is required';
    }
    if (!formData.returnFlightNumber) {
      newErrors.returnFlightNumber = translations?.flightValidation?.returnFlightRequired || 'Return flight number is required';
    }
    // Add more validations as needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate() || !flight) return;
    
    setIsLoading(true);
    try {
      await updateFlight(flight.id, formData);
      toast.success(translations?.editFlightModal?.success || 'Flight updated successfully');
      onClose();
    } catch (error: any) {
      toast.error(error.message || translations?.editFlightModal?.error || 'Failed to update flight');
      console.error('Error updating flight:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!flight) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={translations?.editFlightModal?.title || 'Edit Flight Information'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Contract Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.flightFields?.contractType || 'Contract Type'}
            </label>
            <select
              name="contractType"
              value={formData.contractType || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="B2B">B2B</option>
              <option value="B2C">B2C</option>
            </select>
          </div>

          {/* IATA Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.flightFields?.iataNumber || 'IATA Number'}
            </label>
            <input
              type="text"
              name="iataNumber"
              value={formData.iataNumber || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            {errors.iataNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.iataNumber}</p>
            )}
          </div>

          {/* PNR Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.flightFields?.pnrNumber || 'PNR Number'}
            </label>
            <input
              type="text"
              name="pnrNumber"
              value={formData.pnrNumber || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            {errors.pnrNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.pnrNumber}</p>
            )}
          </div>

          {/* Arrival Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.flightFields?.arrivalDate || 'Arrival Date'}
            </label>
            <input
              type="text"
              name="arrivalDate"
              value={formData.arrivalDate || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="DD/MM/YYYY"
            />
            {errors.arrivalDate && (
              <p className="mt-1 text-sm text-red-600">{errors.arrivalDate}</p>
            )}
          </div>

          {/* Outbound Flight Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.flightFields?.outboundFlightNumber || 'Outbound Flight Number'}
            </label>
            <input
              type="text"
              name="outboundFlightNumber"
              value={formData.outboundFlightNumber || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            {errors.outboundFlightNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.outboundFlightNumber}</p>
            )}
          </div>

          {/* Outbound Departure */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.flightFields?.outboundDeparture || 'Outbound Departure'}
            </label>
            <input
              type="text"
              name="outboundDeparture"
              value={formData.outboundDeparture || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            {errors.outboundDeparture && (
              <p className="mt-1 text-sm text-red-600">{errors.outboundDeparture}</p>
            )}
          </div>

          {/* Outbound Arrival */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.flightFields?.outboundArrival || 'Outbound Arrival'}
            </label>
            <select
              name="outboundArrival"
              value={formData.outboundArrival || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="Jeddah">Jeddah</option>
              <option value="Madinah">Madinah</option>
              <option value="Taif">Taif</option>
            </select>
            {errors.outboundArrival && (
              <p className="mt-1 text-sm text-red-600">{errors.outboundArrival}</p>
            )}
          </div>

          {/* Outbound Arrival Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.flightFields?.outboundArrivalTime || 'Outbound Arrival Time'}
            </label>
            <input
              type="text"
              name="outboundArrivalTime"
              value={formData.outboundArrivalTime || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="HH:MM:SS AM/PM"
            />
            {errors.outboundArrivalTime && (
              <p className="mt-1 text-sm text-red-600">{errors.outboundArrivalTime}</p>
            )}
          </div>

          {/* Return Flight Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.flightFields?.returnFlightNumber || 'Return Flight Number'}
            </label>
            <input
              type="text"
              name="returnFlightNumber"
              value={formData.returnFlightNumber || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            {errors.returnFlightNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.returnFlightNumber}</p>
            )}
          </div>

          {/* Return Departure */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.flightFields?.returnDeparture || 'Return Departure'}
            </label>
            <select
              name="returnDeparture"
              value={formData.returnDeparture || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="Jeddah">Jeddah</option>
              <option value="Madinah">Madinah</option>
              <option value="Taif">Taif</option>
            </select>
            {errors.returnDeparture && (
              <p className="mt-1 text-sm text-red-600">{errors.returnDeparture}</p>
            )}
          </div>

          {/* Return Departure Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.flightFields?.returnDepartureTime || 'Return Departure Time'}
            </label>
            <input
              type="text"
              name="returnDepartureTime"
              value={formData.returnDepartureTime || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="HH:MM:SS AM/PM"
            />
            {errors.returnDepartureTime && (
              <p className="mt-1 text-sm text-red-600">{errors.returnDepartureTime}</p>
            )}
          </div>

          {/* Return Arrival */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.flightFields?.returnArrival || 'Return Arrival'}
            </label>
            <input
              type="text"
              name="returnArrival"
              value={formData.returnArrival || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            {errors.returnArrival && (
              <p className="mt-1 text-sm text-red-600">{errors.returnArrival}</p>
            )}
          </div>

          {/* Return Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.flightFields?.returnDate || 'Return Date'}
            </label>
            <input
              type="text"
              name="returnDate"
              value={formData.returnDate || ''}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="DD/MM/YYYY"
            />
            {errors.returnDate && (
              <p className="mt-1 text-sm text-red-600">{errors.returnDate}</p>
            )}
          </div>
        </div>

        <div className="flex justify-end mt-6 space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.editFlightModal?.cancel || 'Cancel'}
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className={`px-4 py-2 bg-primary-500 text-white rounded-lg transition-colors ${
              isLoading ? 'bg-primary-300 cursor-not-allowed' : 'hover:bg-primary-600'
            }`}
          >
            {isLoading
              ? translations?.editFlightModal?.saving || 'Saving...'
              : translations?.editFlightModal?.update || 'Update Flight'}
          </button>
        </div>
      </form>
    </Modal>
  );
};